import { ANALYTICS_CONSTANTS } from '../constants';

export interface MonthRange {
  startDate: Date;
  endDate: Date;
}

// Months are handled as a single index (year * 12 + month) in the user's local calendar, the
// same calendar the month picker shows.
const toIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();
const monthStart = (index: number) => new Date(Math.floor(index / 12), index % 12, 1);
const monthEnd = (index: number) => new Date(Math.floor(index / 12), (index % 12) + 1, 0);

const EARLIEST_INDEX = ANALYTICS_CONSTANTS.EARLIEST_DATA_YEAR * 12;
const currentIndex = () => toIndex(new Date());

const build = (start: number, end: number): MonthRange => ({
  startDate: monthStart(start),
  endDate: monthEnd(end),
});

export const countMonths = (range: MonthRange) =>
  toIndex(range.endDate) - toIndex(range.startDate) + 1;

// Whole months, not in the future, not before the earliest data, at most maxMonths long.
// A missing end defaults to the current month; a missing start to the longest allowed range
// up to the default lookback.
export function normalizeRange(
  range: { startDate?: Date; endDate?: Date },
  maxMonths: number,
): MonthRange {
  const now = currentIndex();
  const end = Math.max(EARLIEST_INDEX, Math.min(range.endDate ? toIndex(range.endDate) : now, now));
  const defaultSpan = Math.min(maxMonths, ANALYTICS_CONSTANTS.DEFAULT_MONTHLY_LOOKBACK_MONTHS);
  let start = range.startDate ? toIndex(range.startDate) : end - (defaultSpan - 1);
  start = Math.max(EARLIEST_INDEX, Math.min(start, end), end - (maxMonths - 1));
  return build(start, end);
}

// Picking one end moves the other when needed, so any month stays selectable and a
// limited window can be slid forward or back.
export function withStartMonth(range: MonthRange, date: Date, maxMonths: number): MonthRange {
  const start = toIndex(date);
  let end = Math.max(toIndex(range.endDate), start);
  end = Math.min(end, start + (maxMonths - 1), currentIndex());
  return build(start, end);
}

export function withEndMonth(range: MonthRange, date: Date, maxMonths: number): MonthRange {
  const end = toIndex(date);
  let start = Math.min(toIndex(range.startDate), end);
  start = Math.max(start, end - (maxMonths - 1), EARLIEST_INDEX);
  return build(start, end);
}

// YYYY-MM-DD from local date parts. The shared filter serializer uses UTC getters, which
// shift local month boundaries by a day for users east of UTC.
export const toDateParam = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
